<template>
  <div
    class="task-detail-assignment-card"
    :data-testid="`setup-task-detail__assignment-card--${assignment.assignment_number}`"
  >
    <div class="task-detail-assignment-card__header">
      <h6
        class="task-detail-assignment-card__title ma-0"
        data-testid="setup-task-detail__assignment-card-title"
      >
        Assignment {{ assignment.assignment_number }}*
      </h6>
    </div>

    <div
      class="task-detail-assignment-card__meta d-flex align-center justify-space-between"
      data-testid="setup-task-detail__assignment-locations"
    >
      <div class="task-detail-assignment-card__locations d-flex align-center">
        <img class="task-detail-assignment-card__location-icon" src="/img/icons/fbc-icons/LocationIconBlack.svg" alt="Location" />
        <span class="task-detail-assignment-card__location-text">
          {{ locationNamesLabel }}
        </span>
      </div>

      <div
        v-if="showAutoApproveChip || showThresholdChip"
        class="task-detail-assignment-card__chips d-inline-flex align-center flex-wrap"
      >
        <span
          v-if="showAutoApproveChip"
          class="task-detail-assignment-card__auto-approve-badge d-inline-flex align-center"
          data-testid="setup-task-detail__assignment-auto-approve-chip"
        >
          {{ autoApproveChipLabel }}
        </span>
        <span
          v-if="showThresholdChip"
          class="task-detail-assignment-card__threshold-chip d-inline-flex align-center"
          data-testid="setup-task-detail__assignment-threshold-chip"
        >
          <v-icon size="16" color="#38b000" class="mr-1">mdi-check</v-icon>
          Threshold enforced
        </span>
      </div>
    </div>

    <div
      class="task-detail-assignment-card__section"
      data-testid="setup-task-detail__assignment-preparers"
    >
      <p class="task-detail-assignment-card__user-label mb-0">Preparer</p>
      <div class="task-detail-assignment-card__user-pills d-flex flex-wrap">
        <div
          v-for="(user, index) in preparerUsers"
          :key="`preparer-${user._id}-${index}`"
          class="task-detail-assignment-card__user-pill d-flex align-center"
          :data-testid="`setup-task-detail__assignment-preparer--${index}`"
        >
          <span class="task-detail-assignment-card__avatar d-inline-flex align-center justify-center">
            {{ userInitial(user) }}
          </span>
          <span class="task-detail-assignment-card__user-name">{{ user.name }}</span>
        </div>
        <span v-if="!preparerUsers.length" class="task-detail-assignment-card__empty">—</span>
      </div>
    </div>

    <div
      class="task-detail-assignment-card__section"
      :class="{
        'task-detail-assignment-card__section--last': !showSignOffSection,
        'task-detail-assignment-card__section--with-sign-off': showSignOffSection,
      }"
      data-testid="setup-task-detail__assignment-approvers"
    >
      <p class="task-detail-assignment-card__user-label mb-0">Approver</p>
      <div class="task-detail-assignment-card__user-pills d-flex flex-wrap">
        <div
          v-for="(user, index) in approverUsers"
          :key="`approver-${user._id}-${index}`"
          class="task-detail-assignment-card__user-pill d-flex align-center"
          :data-testid="`setup-task-detail__assignment-approver--${index}`"
        >
          <span class="task-detail-assignment-card__avatar d-inline-flex align-center justify-center">
            {{ userInitial(user) }}
          </span>
          <span class="task-detail-assignment-card__user-name">{{ user.name }}</span>
        </div>
        <span v-if="!approverUsers.length" class="task-detail-assignment-card__empty">—</span>
      </div>

      <div
        v-if="showSignOffSection"
        class="task-detail-assignment-card__sign-off"
        data-testid="setup-task-detail__assignment-sign-off-approvers"
      >
        <div class="task-detail-assignment-card__sign-off-inner">
          <div class="task-detail-assignment-card__sign-off-header d-flex align-center">
            <span class="task-detail-assignment-card__sign-off-label">{{ signOffSectionLabel }}</span>
            <span
              class="task-detail-assignment-card__sign-off-chip"
              data-testid="setup-task-detail__assignment-sign-off-chip"
            >
              {{ signOffAnyOneOfLabel }}
            </span>
          </div>
          <div class="task-detail-assignment-card__user-pills task-detail-assignment-card__user-pills--sign-off d-flex flex-wrap">
            <div
              v-for="(user, index) in signOffUsers"
              :key="`sign-off-${user._id}-${index}`"
              class="task-detail-assignment-card__user-pill d-flex align-center"
              :data-testid="`setup-task-detail__assignment-sign-off-approver--${index}`"
            >
              <span class="task-detail-assignment-card__avatar d-inline-flex align-center justify-center">
                {{ userInitial(user) }}
              </span>
              <span class="task-detail-assignment-card__user-name">{{ user.name }}</span>
            </div>
            <span v-if="!signOffUsers.length" class="task-detail-assignment-card__empty">—</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import './TaskDetailAssignmentCard.scss';
import { AUTO_APPROVE, SIGN_OFF } from '../../../../../utils/constants';

export default {
  name: 'TaskDetailAssignmentCard',

  props: {
    assignment: {
      type: Object,
      required: true,
    },
    locations: {
      type: Array,
      default: () => [],
    },
    users: {
      type: Array,
      default: () => [],
    },
    showThreshold: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    signOffSectionLabel() {
      return SIGN_OFF.LABEL;
    },

    signOffAnyOneOfLabel() {
      return SIGN_OFF.ANY_ONE_OF_LABEL;
    },

    autoApproveChipLabel() {
      return AUTO_APPROVE.SUMMARY_CHIP;
    },

    showThresholdChip() {
      return this.showThreshold && this.assignment?.enforceThreshold === true;
    },

    showAutoApproveChip() {
      return !!this.assignment?.auto_approve;
    },

    locationNamesLabel() {
      const names = (this.assignment?.location_ids || [])
        .map((id) => this.resolveLocationName(id))
        .filter(Boolean);
      return names.length ? names.join(', ') : '—';
    },

    preparerUsers() {
      return this.resolveUsers(this.assignment?.preparer);
    },

    approverUsers() {
      return this.resolveUsers(this.assignment?.approver);
    },

    signOffUsers() {
      return this.resolveUsers(this.assignment?.sign_off_approvers);
    },

    showSignOffSection() {
      return !!(
        this.assignment?.requires_sign_off
        || (this.assignment?.sign_off_approvers || []).length
      );
    },
  },

  methods: {
    resolveLocationName(locationId) {
      const match = (this.locations || []).find(
        (loc) => String(loc._id) === String(locationId) || String(loc.id) === String(locationId)
      );
      return match?.name || match?.description || locationId || '';
    },

    resolveUsers(userIds) {
      const ids = Array.isArray(userIds) ? userIds : [];
      return ids
        .map((id) => {
          const userId = typeof id === 'object' ? id._id || id.id : id;
          const match = (this.users || []).find(
            (user) => String(user._id) === String(userId) || String(user.id) === String(userId)
          );
          if (match) {
            return {
              ...match,
              name: match.name || match.displayname || match.email || String(userId),
            };
          }
          if (typeof id === 'object' && id?.name) return id;
          return userId ? { _id: userId, name: String(userId) } : null;
        })
        .filter(Boolean);
    },

    userInitial(user) {
      const name = (user?.name || '').trim();
      if (!name) return '?';
      return name.charAt(0).toUpperCase();
    },
  },
};
</script>
